import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faBell, faCheck, faWrench, faDownload } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as AnalysisIcon } from "./assets/progress-arrow.svg";
import { ReactComponent as ServicesIcon } from "./assets/services.svg";
import "./Home.css";
import HomePreviewCarousel from "./HomePreviewCarousel";
import { SERVICE_HISTORY_PAGES } from "./serviceHistoryRoutes";
import { serviceLogos } from "./serviceLogos";

const featuredServices = ["teams", "zoom", "webex", "googlemeet", "slack"].map(key => SERVICE_HISTORY_PAGES[key]);

const capabilities = [
  { Icon: AnalysisIcon, title: "Find the weak link", text: "Understand device, network, and service performance so you know where connection issues begin." },
  { Icon: ServicesIcon, title: "Keep your team connected", text: "Share connection data to find who needs help boosting their signal." },
  { icon: faBell, title: "Stay ahead of downtime", text: "Follow the services your team depends on and see which features are affected by an incident." },
  { icon: faWrench, title: "Fix issues", text: "Analysis and reports find issues and provide insights to resolve them, keeping your team connected." },
];

function Field({ id, label, type = "text", autoComplete }) {
  return <label className="auth-field" htmlFor={id}><span>{label}</span>
    <input id={id} type={type} placeholder={label} autoComplete={autoComplete} />
  </label>;
}

const Home = ({ loggedIn, loginClick, addUserClick, showLoginForm, showLoginClick,
  showUserCreationForm, showUserCreationClick, showForgotPasswordForm, showForgotPasswordClick,
  requestPasswordReset, hideHomePageFormsClick, loginOnly = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const panelRef = useRef(null);
  const closeRef = useRef(hideHomePageFormsClick);
  closeRef.current = hideHomePageFormsClick;
  const formMode = !loggedIn && showLoginForm ? "login" : showForgotPasswordForm ? "reset" : showUserCreationForm ? "signup" : null;
  const isFormOpen = Boolean(formMode);

  // Shared by the home modal and the embedded Downloads/Plans login dialogs.
  useEffect(() => {
    if (!isFormOpen) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current?.();
      }
      if (event.key !== "Tab") return;
      const controls = panelRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href]');
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || !panelRef.current.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !panelRef.current.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown, true);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isFormOpen]);

  useEffect(() => {
    setFormError("");
    panelRef.current?.querySelector("input")?.focus();
  }, [formMode]);

  const submitForm = async event => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormError("");
    try {
      if (formMode === "signup") {
        const email = document.getElementById("email").value;
        const password = document.getElementById("password").value;
        const result = await addUserClick();
        if (result === "success") await loginClick(email, password);
        else setFormError("Account creation failed. Please check your details and try again.");
      } else if (formMode === "reset") await requestPasswordReset();
      else await loginClick();
    } catch {
      setFormError("We couldn't complete your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const authPanel = formMode && <section ref={panelRef}
    className={`auth-panel${formMode === "signup" ? " auth-panel--signup" : ""}`}
    role={loginOnly ? undefined : "dialog"} aria-modal={loginOnly ? undefined : "true"}
    aria-labelledby={loginOnly ? undefined : "auth-heading"}>
    {formMode === "signup" && <aside className="auth-introduction">
      <img className="auth-logo" src={`${process.env.PUBLIC_URL}/logo512.png`} alt="" />
      <p className="home-eyebrow">BETTER TOGETHER</p>
      <h2>Keep your team connected.</h2>
      <p>From anywhere, at any time. Get a clearer view of the devices, networks, and services behind every meeting.</p>
      <ul>{["Understand your connection", "Monitor the tools you depend on", "Stay informed with team alerts"].map(text =>
        <li key={text}><FontAwesomeIcon icon={faCheck} aria-hidden="true" />{text}</li>)}</ul>
    </aside>}
    <div className="auth-content">
      <p className="home-eyebrow">YOUR SIGNALTUNER ACCOUNT</p>
      <h2 id="auth-heading">{formMode === "login" ? "Welcome back." : formMode === "signup" ? "Create Account" : "Reset Password"}</h2>
      <p className="auth-description">{formMode === "login" ? "Sign in to see how your team is connecting." : formMode === "signup" ? "Better connections start here." : "We'll help you get back to your account."}</p>
      <form onSubmit={submitForm} noValidate>
        {formMode === "login" && <>
          <Field id="loginEmail" label="Email" type="email" autoComplete="username" />
          <Field id="loginPassword" label="Password" type="password" autoComplete="current-password" />
          <button className="auth-text-button auth-forgot" type="button" onClick={showForgotPasswordClick}>Forgot Password?</button>
        </>}
        {formMode === "signup" && <>
          <div className="auth-name-fields">
            <Field id="firstName" label="First name" autoComplete="given-name" />
            <Field id="lastName" label="Last name" autoComplete="family-name" />
          </div>
          <Field id="email" label="Email" type="email" autoComplete="email" />
          <div className="auth-name-fields">
            <Field id="password" label="Password" type="password" autoComplete="new-password" />
            <Field id="confirmPassword" label="Confirm Password" type="password" autoComplete="new-password" />
          </div>
        </>}
        {formMode === "reset" && <>
          <Field id="resetEmail" label="Email" type="email" autoComplete="email" />
          <p className="auth-description">If your email address is registered with us, we'll send you an email to allow you to reset your password.</p>
        </>}
        {formError && <p className="auth-error" role="alert">{formError}</p>}
        <button className="home-button home-button--primary auth-submit" type="submit" disabled={isSubmitting}>
          {isSubmitting ? (formMode === "signup" ? "Creating Account..." : "Please wait...") : formMode === "login" ? "Login" : formMode === "signup" ? "Create Account" : "Send Password Reset Email"}
        </button>
      </form>
      <div className="auth-switch">
        <p>{formMode === "login" ? "Don't have an account?" : formMode === "signup" ? "Already have an account?" : "Remember your password?"}</p>
        <button className="home-button home-button--secondary" type="button" onClick={formMode === "login" ? showUserCreationClick : showLoginClick}>
          {formMode === "login" ? "Create New Account" : "Login"}
        </button>
      </div>
    </div>
    <button className="auth-close hide-home-page-forms-button" type="button" aria-label="Close dialog" onClick={hideHomePageFormsClick}>&times;</button>
  </section>;

  if (loginOnly) return authPanel;

  return <>
    <main className="home-page">
      <div className="home-inner">
        <section className="home-hero" aria-labelledby="home-heading">
          <div className="home-hero-copy">
            <p className="home-eyebrow">CONNECTION CLARITY. TEAM CONFIDENCE.</p>
            <h1 id="home-heading">Better connections.<br /><span>Better meetings.</span></h1>
            <p className="home-intro">Find what's getting between your team and a great meeting. Understand your devices, networks, and services with SignalTuner.</p>
            <div className="home-actions">
              {loggedIn ? <Link className="home-button home-button--primary" to="/dashboard">Go to dashboard <FontAwesomeIcon icon={faArrowRight} /></Link> : <>
                <button className="home-button home-button--primary" onClick={showUserCreationClick}>Sign up <FontAwesomeIcon icon={faArrowRight} /></button>
                <button className="home-button home-button--secondary" onClick={showLoginClick}>Login</button>
              </>}
            </div>
            <Link className="home-download-link" to="/downloads">Get the desktop app <FontAwesomeIcon icon={faDownload} aria-hidden="true" /></Link>
          </div>
          <HomePreviewCarousel paused={isFormOpen} />
        </section>
        <section className="home-services" aria-label="Meeting and collaboration services">
          <p>BETTER CONNECTIONS FOR THE TOOLS YOU USE</p>
          <ul>{featuredServices.map(({ displayName, logo }) => <li key={logo}>
            <img src={serviceLogos[logo]} alt="" />
            <span>{displayName}</span>
          </li>)}</ul>
        </section>
        <section className="home-capabilities" aria-labelledby="home-capabilities-heading">
          <div className="home-section-heading"><div><p className="home-eyebrow">THE WHOLE PICTURE</p><h2 id="home-capabilities-heading">Less guesswork. More connection.</h2></div><p>See the health of the devices and services that power your team's day.</p></div>
          <div className="home-feature-grid">{capabilities.map(({ icon, Icon, title, text }) => <article className="home-feature" key={title}>
            <div className="home-feature-top"><span className="home-feature-icon">{Icon ? <Icon className="home-feature-custom-icon" aria-hidden="true" focusable="false" /> : <FontAwesomeIcon icon={icon} aria-hidden="true" />}</span></div>
            <h3>{title}</h3><p>{text}</p>
          </article>)}</div>
        </section>
        <section className="home-next-step" aria-labelledby="home-next-heading">
          <div><p className="home-eyebrow">FROM INSIGHT TO ACTION</p><h2 id="home-next-heading">Keep your team on mission.</h2><p>Start with the desktop app. Analyze your team's connection. Explore Connectivity Health Reports when you're ready to look deeper.</p></div>
          <div className="home-actions"><Link className="home-button home-button--primary" to="/downloads">Download SignalTuner</Link><Link className="home-button home-button--secondary" to="/plans">Explore plans <FontAwesomeIcon icon={faArrowRight} /></Link></div>
        </section>
        <footer className="home-footer"><span>SignalTuner · Better connected.</span><Link to="/support">Need a hand? Contact support <span aria-hidden="true">↗</span></Link></footer>
      </div>
    </main>
    {authPanel && <div className="home-auth-backdrop" onMouseDown={event => {
      if (event.target === event.currentTarget) hideHomePageFormsClick();
    }}>{authPanel}</div>}
  </>;
};

export default Home;
